import { NotificationProgrammatic as Notification } from "buefy";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

/**
 * Devuelve el `detail` de un error de DRF si vino en la respuesta, o el
 * fallback provisto. Pensado para callers (acciones Vuex, vistas) que
 * setean el mensaje de error en el estado para mostrarlo inline. NO
 * dispara notification ni throw — para ese flujo usar `consoleHandlerError`.
 */
export function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail ?? fallback;
}

/**
 * True para cancelaciones de axios (AbortController). No son errores: son el
 * control de tráfico esperado de `createTakeLatest`, que aborta el request en
 * vuelo cuando uno más nuevo lo reemplaza (cambio rápido de filtros, cambio
 * de vista clasificador ↔ Libro Mayor). Mismo criterio que `axios.isCancel`
 * sin importar axios acá.
 */
export function isCanceledError(error) {
    return Boolean(
        error &&
        (error.code === "ERR_CANCELED" ||
            error.name === "CanceledError" ||
            error.__CANCEL__),
    );
}

/**
 * True para errores que el interceptor de api.js ya resolvió como cierre o
 * renovación de sesión (403 "Token is blacklisted" / 401): el interceptor
 * mostró su propio aviso y programó la redirección, así que el toast rojo
 * genérico sobraría — la app no falló, se protegió.
 */
export function isAuthRedirectError(error) {
    return Boolean(error && error.isAuthRedirect);
}

export function consoleHandlerError({
    error = null,
    refFields = Object,
    customDefaultMessage = null,
    empty = false,
    indefinite = false,
    // Opt-in opcional. Cuando `silent` es true, suprime la notificación
    // visual y el re-throw — pensado para callers (ej. acciones Vuex) que ya
    // gestionan el error vía estado (`commit("SET_ERROR", ...)`) y no quieren
    // duplicar UX ni rechazar la promesa. El default mantiene el
    // comportamiento histórico para no romper consumidores existentes.
    silent = false,
    // Opt-out del re-throw conservando log y notificación: para callers que
    // ya manejaron el error en su `.catch` (ej. FormLogin ante un 400 por
    // contraseña incorrecta) y no quieren que la promesa siga rechazada —
    // ese rechazo llegaba a window.onunhandledrejection y Sentry lo contaba
    // como error (CFO_REMOTO_FRONTEND-9P, 169 usuarios).
    rethrow = true,
    // Prefijo opcional para los logs (ej. "[salesReport]"). Útil para
    // identificar de qué módulo proviene un error en la consola del browser.
    prefix = "",
}) {
    // Cancelación intencional (take-latest abortó un request superado) o
    // sesión ya redirigida por el interceptor: ni log ni notificación ni
    // re-throw — tratarlas como error sólo mete ruido.
    if (isCanceledError(error) || isAuthRedirectError(error)) return;
    const tag = prefix ? `${prefix} ` : "";
    console.error(`${tag}${JSON.stringify(error)}`);
    console.error(
        `${tag}${JSON.stringify(error?.response?.data) || "No hay detalle del error"}`
    );
    console.error(`${tag}${error?.response?.status || "Sin retorno del estado"}`);
    console.error(`${tag}${error?.response?.statusText || "Sin detalle del estado"}`);
    if (silent) return;
    let msg = empty ? null : error?.response?.data || null;
    showErrorMessage(msg, refFields, customDefaultMessage, indefinite);
    if (rethrow) throw error;
}

export function notificate(
    message,
    type,
    position = "is-top-right",
    indefinite = false,
    duration = 5000
) {
    Notification.open({
        // Buefy lo renderiza con v-html: sólo pasa el formato permitido.
        message: sanitizeHtml(message),
        type: type,
        position: position,
        ...(!indefinite && {
            duration: duration,
        }),
        ...(indefinite && {
            indefinite: indefinite,
        }),
    });
}

function showErrorMessage(
    innerMessage = null,
    refFields = Object,
    customDefaultMessage = null,
    indefinite = false,
) {
    let message = "";
    if (isObject(innerMessage)) {
        let additionalKeys = ["counterpart", "owner", "company"];
        let expectedKeys = Object.keys(refFields).concat(additionalKeys);
        Object.entries(innerMessage).forEach(function([key, value]) {
            if (expectedKeys.includes(key)) {
                message = setFormError(
                    refFields,
                    key,
                    value,
                    customDefaultMessage
                );
            } else {
                let firstMessage = Array.isArray(value) ? value[0] : value;
                message += showErrorMessage(
                    firstMessage, refFields, null, indefinite
                );
            }
        });
        return message;
    } else {
        return notificateInnerMessageOrDefault(
            innerMessage,
            customDefaultMessage,
            indefinite,
        );
    }
}

function setFormError(
    refFields,
    refName,
    errorMessage,
    customDefaultMessage = null
) {
    if (isObject(errorMessage) && !Array.isArray(errorMessage)) {
        Object.entries(errorMessage).forEach(function([key, value]) {
            let fieldName = `${refName}_${key}`;
            return setFormError(
                refFields,
                fieldName,
                value,
                customDefaultMessage
            );
        });
    } else {
        return fillFormErrorElementOrDefault(
            refFields,
            refName,
            errorMessage,
            customDefaultMessage
        );
    }
}

function fillFormErrorElementOrDefault(
    refFields,
    refName,
    message,
    defaultMessage = null
) {
    let msg = "";
    if (refFields[refName] && refFields[refName].$children) {
        let msg = defaultMessage || message;
        refFields[refName].$children[0].setErrors(msg);
    } else {
        let field = translateFieldName(refName);
        msg = notificateInnerMessageOrDefault(`${field}: ${message}`);
    }
    return msg;
}

function notificateInnerMessageOrDefault(
    message = null,
    customDefaultMessage = null,
    indefinite = false,
) {
    let defaultMessage = `
        <p class="m-0">
            <strong>Lo sentimos, estamos teniendo un pequeño error.</strong>
        </p><br>
        <p class="m-0">Ya estamos trabajando para mejorar la situación y
            nos tomará un momento para solucionarlo.
        </p>
    `;
    let msg = customDefaultMessage || message || defaultMessage;
    if (msg.includes("Server Error (500)")) {
        msg = defaultMessage;
    }
    notificate(`<p class="m-0">${msg}</p>`, "is-danger is-light", "is-top-right", indefinite);
}

function translateFieldName(fieldName) {
    return {
        financial_entity: "Institución Financiera",
        amount: "Monto",
    }[fieldName];
}

function isObject(param) {
    return typeof param === "object" && param !== null;
}
